import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Bell,
  Plug,
  Search,
  Building2,
  Settings,
  ShieldCheck,
  Cloud,
  Users,
  BarChart3,
  BookOpen,
  Mail,
  ScrollText,
  Bug,
  Presentation,
  Server,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  mspOnly?: boolean;
  superadminOnly?: boolean;
  adminOnly?: boolean;
  mvpHidden?: boolean; // MVP launch: kept in code (page/route intact) but not shown in GUI
}

const navItems: NavItem[] = [
  { label: "Dashboard", href: "/app", icon: LayoutDashboard },
  { label: "Alerts", href: "/app/alerts", icon: Bell },
  { label: "Integrations", href: "/app/integrations", icon: Plug },
  { label: "Hosts", href: "/app/hosts", icon: Server },
  { label: "Investigation", href: "/app/investigation", icon: Search },
  { label: "Threat Intel", href: "/app/threat-intel", icon: Globe, mvpHidden: true },
  { label: "Logs", href: "/app/logs", icon: ScrollText },
  { label: "MSP Portal", href: "/app/msp", icon: Building2, mspOnly: true },
  { label: "Email Auth", href: "/app/email-auth", icon: Mail, mvpHidden: true },
  { label: "Vulnerabilities", href: "/app/vulnerabilities", icon: Bug, mvpHidden: true },
  { label: "Cloud Posture", href: "/app/cloud-posture", icon: Cloud, mvpHidden: true },
  { label: "Executive", href: "/app/executive", icon: Presentation, adminOnly: true },
  { label: "Usage", href: "/app/usage", icon: BarChart3, adminOnly: true },
  { label: "Settings", href: "/app/settings", icon: Settings },
  { label: "Docs", href: "/docs", icon: BookOpen },
  { label: "Platform Admin", href: "/app/superadmin", icon: ShieldCheck, superadminOnly: true },
  { label: "All Tenants", href: "/app/superadmin/tenants", icon: Building2, superadminOnly: true },
  { label: "All Users", href: "/app/superadmin/users", icon: Users, superadminOnly: true },
];

interface SidebarProps {
  /** Called on any nav link click — lets the mobile drawer close on navigation. */
  onNavigate?: () => void;
  /** Route prefix used by the temporary authenticated classic fallback. */
  basePath?: string;
}

export function Sidebar({ onNavigate, basePath = "/app" }: SidebarProps) {
  const { user } = useAuth();
  const isMsp = user?.roles?.includes("msp") ?? false;
  const isSuperadmin = user?.isSuperadmin ?? false;
  const isAdmin = user?.roles?.includes("admin") || user?.roles?.includes("owner") || false;

  // Separate regular and superadmin items for visual grouping
  const regularItems = navItems.filter(
    (item) => !item.superadminOnly && !item.mvpHidden,
  );
  const superadminItems = navItems.filter((item) => item.superadminOnly);

  return (
    <nav className="flex flex-col gap-1 px-2 py-4">
      {regularItems
        .filter((item) => !item.mspOnly || isMsp)
        .filter((item) => !item.adminOnly || isAdmin)
        .map((item) => {
          const href = item.href.replace(/^\/app/, basePath);
          return (
            <NavLink
              key={item.href}
              to={href}
              end={item.href === "/app"}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

      {/* Superadmin section */}
      {isSuperadmin && superadminItems.length > 0 && (
        <>
          <div className="mx-3 my-2 border-t" />
          <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Superadmin
          </span>
          {superadminItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href.replace(/^\/app/, basePath)}
              end={item.href === "/app/superadmin"}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-red-500/10 text-red-500"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </>
      )}
    </nav>
  );
}
